import {
  DndContext, DragOverlay, closestCenter, pointerWithin,
  KeyboardSensor, PointerSensor,
  useSensor, useSensors, useDroppable,
  type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Crown, Eye, FileText, LayoutTemplate, Loader2, Plus, Save, Share2 } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useBlocker, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { OPEN_ACCESS } from '@/features/records/components/FieldAccessEditor'
import { isDataField } from '@/features/records/utils/fieldAccess'
import { useForm as useFormHook, useFormTemplates, useFormTemplateMutations, useAllForms, useFormById } from '@/hooks/useForm'
import { usePermission } from '@/hooks/usePermission'
import { NewRecordPreview } from '@/routes/record-groups/NewRecordDialog'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { FormField, FormTemplate } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { handleApiError } from '@/utils/error-parser'

import { CanvasFieldCard } from './CanvasFieldCard'
import {
  CanvasEmptyZone, CanvasEndDropZone, CanvasFieldCardOverlay, CanvasTopDropZone,
  DragOverlayCard, DropIndicator,
} from './DesignerCanvasParts'
import { FieldTypePalette } from './FieldTypePalette'
import { FieldTypePickerSheet } from './FieldTypePickerSheet'
import { SaveTemplateDialog } from './SaveTemplateDialog'
import { ShareTab } from './ShareTab'
import { FIELD_CONFIG, PALETTE_ITEMS } from './fieldTypeConfig'

type DesignerTab = 'design' | 'preview' | 'share'

const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/
const FIELD_KEY_HINT = 'Use lowercase letters, numbers and underscores; start with a letter.'
const LAYOUT_DEFAULTS: Partial<Record<FormFieldType, Record<string, unknown>>> = {
  [FormFieldType.SECTION_HEADING]: { text: 'New section', size: 'h2' },
  [FormFieldType.STATIC_TEXT]: { text: 'Write **instructions** here.' },
}

function fieldKeyError(key: string): string | null {
  if (!key.trim()) return 'Key is required.'
  return FIELD_KEY_PATTERN.test(key) ? null : FIELD_KEY_HINT
}

function createField(type: FormFieldType, formId: string, orderIndex: number): FormField {
  const config = FIELD_CONFIG[type]
  return {
    id: crypto.randomUUID(), formId, key: `${type.toLowerCase()}_${Date.now()}`,
    label: config.label, type, required: false,
    options: type === FormFieldType.CHECKBOX
      ? { choices: [], selectionMode: 'MULTIPLE' }
      : LAYOUT_DEFAULTS[type] ?? null,
    validations: null, orderIndex,
  }
}

export function FormDesigner() {
  const { recordGroupId } = useParams<{ recordGroupId: string }>()
  const { form, isLoading, create, update, isCreating, isUpdating } = useFormHook(recordGroupId!)
  const { data: templates, isLoading: templatesLoading, isError: templatesError } = useFormTemplates()
  const { data: allForms = [] } = useAllForms(recordGroupId!)
  const [selectedFormId, setSelectedFormId] = useState('')
  const { data: selectedForm } = useFormById(selectedFormId || '')
  const { isOwner, isAdmin, isDeveloper, can } = usePermission()
  const templateMutations = useFormTemplateMutations()
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<FormTemplate | null>(null)
  const canSaveTemplate = isDeveloper || can(PERMISSION_KEYS.FORM_TEMPLATE_CREATE)
  const canDeleteTemplate = (template: FormTemplate) => template.scope === 'PLATFORM' ? isDeveloper : can(PERMISSION_KEYS.FORM_TEMPLATE_DELETE)
  const canManageAccess = isOwner || isAdmin

  const [activeTab, setActiveTab] = useState<DesignerTab>('design')
  const [fields, setFields] = useState<FormField[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const blocker = useBlocker(isDirty)
  const [activeDrag, setActiveDrag] = useState<{ source: 'palette' | 'canvas'; fieldType?: FormFieldType; field?: FormField } | null>(null)
  const [paletteInsertIndex, setPaletteInsertIndex] = useState<number | null>(null)
  const [mainFieldId, setMainFieldId] = useState<string | null>(null)
  const [scrollToFieldId, setScrollToFieldId] = useState<string | null>(null)
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  )
  const [isFieldPickerOpen, setIsFieldPickerOpen] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches)
    updateViewport()
    mediaQuery.addEventListener('change', updateViewport)
    return () => mediaQuery.removeEventListener('change', updateViewport)
  }, [])

  useEffect(() => {
    if (!selectedFormId) {
      if (form?.id) setSelectedFormId(form.id)
      else if (allForms.length) setSelectedFormId(allForms[0].id)
    }
  }, [form, allForms, selectedFormId])

  const sourceForm = selectedForm ?? form
  const sourceFormRef = useRef(sourceForm)
  sourceFormRef.current = sourceForm
  // Key by the form actually being edited. The selected current form can finish
  // loading after `form`; keying by both IDs reset dirty edits a second time.
  const sourceFormKey = sourceForm?.id ?? ''
  useEffect(() => {
    const source = sourceFormRef.current
    if (source?.fields) {
      setFields([...source.fields].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)))
      setMainFieldId(source.mainFieldId ?? null)
      setIsDirty(false)
    }
  }, [sourceFormKey])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const { setNodeRef: setPanelRef } = useDroppable({ id: 'palette-panel' })
  const collisionDetectionStrategy: CollisionDetection = (args) =>
    args.active?.data?.current?.source === 'palette' ? pointerWithin(args) : closestCenter(args)

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current
    if (data?.source === 'palette') setActiveDrag({ source: 'palette', fieldType: data.fieldType })
    else if (data?.source === 'canvas') setActiveDrag({ source: 'canvas', field: data.field })
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (active.data.current?.source !== 'palette') return
    if (!over || over.id === 'palette-panel') { setPaletteInsertIndex(null); return }
    if (over.id === 'canvas-droppable' || over.id === 'canvas-top') { setPaletteInsertIndex(0); return }
    if (over.id === 'canvas-end') { setPaletteInsertIndex(fields.length); return }
    const overIdx = fields.findIndex(field => field.id === over.id)
    if (overIdx < 0) { setPaletteInsertIndex(fields.length); return }
    const activeRect = event.active.rect.current.translated
    if (!activeRect) { setPaletteInsertIndex(overIdx); return }
    const activeCenterY = activeRect.top + activeRect.height / 2
    const overMidY = over.rect.top + over.rect.height / 2
    setPaletteInsertIndex(activeCenterY < overMidY ? overIdx : overIdx + 1)
  }

  const handleDragCancel = () => { setActiveDrag(null); setPaletteInsertIndex(null) }
  const isCanvasTarget = (id: string | number | null | undefined) =>
    !!id && (id === 'canvas-droppable' || id === 'canvas-end' || id === 'canvas-top' || fields.some(field => field.id === id))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveDrag(null)
    if (active.data.current?.source === 'palette') {
      const insertAt = paletteInsertIndex ?? fields.length
      setPaletteInsertIndex(null)
      if (!over || !isCanvasTarget(over.id)) return
      const newField = createField(active.data.current?.fieldType as FormFieldType, form?.id || '', insertAt)
      setFields(current => {
        const next = [...current]
        next.splice(insertAt, 0, newField)
        return next.map((field, index) => ({ ...field, orderIndex: index }))
      })
      setIsDirty(true)
    } else if (active.data.current?.source === 'canvas' && over && active.id !== over.id) {
      setFields(current => {
        const from = current.findIndex(field => field.id === active.id)
        const to = current.findIndex(field => field.id === over.id)
        return from < 0 || to < 0 ? current : arrayMove(current, from, to).map((field, index) => ({ ...field, orderIndex: index }))
      })
      setIsDirty(true)
    }
  }

  const addFieldAtEnd = (type: FormFieldType) => {
    const field = createField(type, form?.id || '', fields.length)
    setFields(current => [...current, field].map((item, index) => ({ ...item, orderIndex: index })))
    setIsDirty(true)
    setScrollToFieldId(field.id)
  }
  useEffect(() => {
    if (!scrollToFieldId) return
    document.querySelector(`[data-field-id="${scrollToFieldId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setScrollToFieldId(null)
  }, [scrollToFieldId, fields])

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(current => current.map(field => field.id === id ? { ...field, ...updates } : field))
    setIsDirty(true)
  }
  const deleteField = (id: string) => {
    setFields(current => current.filter(field => field.id !== id))
    setMainFieldId(current => current === id ? null : current)
    setIsDirty(true)
  }
  const moveField = (id: string, offset: -1 | 1) => {
    setFields(current => {
      const from = current.findIndex(field => field.id === id)
      const to = from + offset
      if (from < 0 || to < 0 || to >= current.length) return current
      const next = [...current]
      const movedField = next[from]
      next[from] = next[to]
      next[to] = movedField
      return next.map((field, index) => ({ ...field, orderIndex: index }))
    })
    setIsDirty(true)
  }
  const applyTemplate = (template: FormTemplate) => {
    const applied = template.fields.map((item, index) => ({
      id: crypto.randomUUID(), formId: form?.id || '', key: item.key, label: item.label,
      type: item.type, required: item.required, options: item.options, validations: item.validations,
      orderIndex: item.orderIndex ?? index,
    }))
    setFields(applied)
    setMainFieldId(applied.find(field => field.key === template.mainFieldKey)?.id ?? null)
    setIsDirty(true)
    toast.success(`Template "${template.name}" applied`)
  }

  const saveAsTemplate = ({ name, description, platform }: { name: string; description: string; platform: boolean }) => {
    const mainFieldKey = mainFieldId ? fields.find(field => field.id === mainFieldId)?.key ?? null : null
    templateMutations.save.mutate({
      formName: name, formDescription: description, platform, mainFieldKey,
      fields: fields.map((field, index) => ({
        key: field.key, label: field.label, type: field.type, required: field.required,
        options: field.options, validations: field.validations, orderIndex: index,
      })),
    }, {
      onSuccess: () => { toast.success(`Template "${name}" saved`); setSaveTemplateOpen(false) },
      onError: (error) => handleApiError(error),
    })
  }

  const handleSave = () => {
    const badKey = fields.find((field) => fieldKeyError(field.key))
    if (badKey) {
      toast.error(`Fix the key of "${badKey.label || badKey.key}"`, { description: fieldKeyError(badKey.key) ?? undefined })
      document.querySelector(`[data-field-id="${badKey.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    const fieldsData = fields.map((field, index) => ({
      key: field.key, label: field.label, type: field.type, required: field.required,
      options: field.options, validations: field.validations, orderIndex: index,
      ...(canManageAccess ? { access: field.access ?? OPEN_ACCESS } : {}),
    }))
    const options = {
      onSuccess: (saved: { id: string }) => { toast.success('Form saved'); setIsDirty(false); setSelectedFormId(saved.id) },
      onError: (error: unknown) => handleApiError(error),
    }
    const targetFormId = selectedFormId || form?.id
    const mainFieldKey = mainFieldId ? fields.find(field => field.id === mainFieldId)?.key ?? null : null
    const payload = { recordGroupId: recordGroupId!, fields: fieldsData, mainFieldId: null, mainFieldKey }
    if (targetFormId) update({ ...payload, id: targetFormId }, options)
    else create(payload, options)
  }

  if (isLoading) return <div className="flex h-full min-h-0"><Skeleton className="hidden h-full w-80 shrink-0 md:block" /><Skeleton className="h-full min-w-0 flex-1" /></div>

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetectionStrategy} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <div className="flex h-full min-h-0 overflow-hidden bg-background text-foreground">
        <aside ref={setPanelRef} className="hidden w-80 min-w-64 shrink-0 flex-col overflow-hidden bg-card md:flex">
          <FieldTypePalette items={PALETTE_ITEMS} onAddField={addFieldAtEnd} templates={templates} templatesLoading={templatesLoading} templatesError={templatesError} onApplyTemplate={applyTemplate} onDeleteTemplate={setTemplateToDelete} canDeleteTemplate={canDeleteTemplate} />
        </aside>

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
          <div className="flex flex-col items-stretch gap-2 px-4 py-3 sm:px-5 md:flex-row md:items-center md:justify-between md:gap-3">
            <nav aria-label="Form designer sections" className="flex w-full max-w-full items-center justify-between gap-1 overflow-x-auto md:w-auto md:justify-normal">
              {([
                { key: 'design', label: 'Design', Icon: FileText },
                { key: 'preview', label: 'Preview', Icon: Eye },
                { key: 'share', label: 'Share', Icon: Share2 },
              ] as const).map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => setActiveTab(key)} aria-current={activeTab === key ? 'page' : undefined}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${activeTab === key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </nav>
            <div className="flex w-full flex-nowrap items-center gap-2 md:w-auto md:justify-end">
              {allForms.length > 0 && <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                <SelectTrigger className="h-9 w-28 shrink-0 md:h-10 md:w-44"><SelectValue placeholder="Version" /></SelectTrigger>
                <SelectContent>{[...allForms].sort((a, b) => b.version - a.version).map(item => <SelectItem key={item.id} value={item.id}>Version {item.version.toFixed(1)}{item.id === form?.id ? ' (Latest)' : ''}</SelectItem>)}</SelectContent>
              </Select>}
              <Button type="button" onClick={() => setIsFieldPickerOpen(true)} className="h-9 min-w-0 flex-1 gap-1 px-2 text-xs md:hidden">
                <Plus className="h-4 w-4" />Add field
              </Button>
              {canSaveTemplate && <Button type="button" variant="outline" onClick={() => setSaveTemplateOpen(true)} disabled={fields.length === 0} className="hidden h-10 gap-2 md:inline-flex">
                <LayoutTemplate className="h-4 w-4" />Save as template
              </Button>}
              <Button onClick={handleSave} disabled={isCreating || isUpdating || !isDirty} className="h-9 min-w-0 flex-1 gap-1 px-2 text-xs md:h-10 md:flex-initial md:gap-2 md:px-4 md:text-sm">
                {isCreating || isUpdating ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : <><Save className="h-4 w-4" /><span className="min-[360px]:hidden md:hidden">Save</span><span className="hidden min-[360px]:inline md:hidden">Save Form</span><span className="hidden md:inline">Save Form</span></>}
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-background">
            {activeTab === 'design' && <div className="flex min-h-full flex-col bg-background">
              <div className="flex flex-1 flex-col gap-4 p-4 sm:p-6">
                {fields.length > 0 && <section className="pb-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600"><Crown className="h-4 w-4" /></span>Main field (record title)</div>
                  <Select value={mainFieldId ?? '__none__'} onValueChange={value => { setMainFieldId(value === '__none__' ? null : value); setIsDirty(true) }}>
                    <SelectTrigger className="h-11 bg-background"><SelectValue placeholder="Choose a title field" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">Automatic (first suitable field)</SelectItem>{fields.filter(field => field.type !== FormFieldType.FILE_UPLOAD && field.type !== FormFieldType.EMAIL_MESSAGE && isDataField(field)).map(field => <SelectItem key={field.id} value={field.id}>{field.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="mt-2 text-xs text-muted-foreground">The submitted value is shown as the record title in lists and detail pages. File fields can't be the title.</p>
                </section>}

                {fields.length === 0 ? <CanvasEmptyZone /> : <>
                  {activeDrag?.source === 'palette' && <CanvasTopDropZone isOver={paletteInsertIndex === 0} />}
                  <SortableContext items={fields} strategy={verticalListSortingStrategy}>
                    {paletteInsertIndex === 0 && <DropIndicator />}
                    <div className="space-y-3">{fields.map((field, index) => <Fragment key={field.id}><CanvasFieldCard field={field} allFields={fields} keyError={fieldKeyError(field.key)} onUpdate={updateField} onDelete={deleteField} canManageAccess={canManageAccess} isTitleField={field.id === mainFieldId} isMobileViewport={isMobileViewport} onMoveUp={index > 0 ? () => moveField(field.id, -1) : undefined} onMoveDown={index < fields.length - 1 ? () => moveField(field.id, 1) : undefined} />{paletteInsertIndex === index + 1 && <DropIndicator />}</Fragment>)}</div>
                  </SortableContext>
                </>}
                {fields.length > 0 && <CanvasEndDropZone isOver={activeDrag?.source === 'palette' && paletteInsertIndex === fields.length} />}
              </div>
            </div>}

            {activeTab === 'preview' && <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-4 sm:px-0">
              <div className="mx-auto flex max-w-lg items-center gap-2 rounded-lg bg-primary/5 px-4 py-3"><Eye className="h-4 w-4 shrink-0 text-primary" /><p className="text-xs font-medium text-primary">The New Record form as your team will see it. Nothing is saved. The public form preview is in the Share tab.</p></div>
              <NewRecordPreview fields={fields} />
            </div>}
            {activeTab === 'share' && <div className="mx-auto w-full max-w-3xl px-4 py-4 sm:px-0">{form ? <ShareTab formId={form.id} recordGroupId={form.recordGroupId} /> : <div className="flex flex-col items-center justify-center rounded-lg bg-card py-16 text-center"><p className="text-sm font-semibold">Save the form first</p><p className="mt-1 text-sm text-muted-foreground">Public links are available after the form is saved.</p></div>}</div>}
          </div>
        </main>
      </div>
      <DragOverlay dropAnimation={null}>{activeDrag?.source === 'palette' && activeDrag.fieldType ? <DragOverlayCard cfg={FIELD_CONFIG[activeDrag.fieldType]} /> : activeDrag?.source === 'canvas' && activeDrag.field ? <CanvasFieldCardOverlay field={activeDrag.field} /> : null}</DragOverlay>
      <FieldTypePickerSheet
        open={isFieldPickerOpen}
        onOpenChange={setIsFieldPickerOpen}
        items={PALETTE_ITEMS}
        onAddField={addFieldAtEnd}
        templates={templates}
        templatesLoading={templatesLoading}
        templatesError={templatesError}
        onApplyTemplate={applyTemplate}
        onDeleteTemplate={setTemplateToDelete}
        canDeleteTemplate={canDeleteTemplate}
      />
      <SaveTemplateDialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen} isDeveloper={isDeveloper} isSaving={templateMutations.save.isPending} onSave={saveAsTemplate} />
      <ConfirmDialog
        open={!!templateToDelete}
        onOpenChange={(open) => { if (!open) setTemplateToDelete(null) }}
        title={`Delete template "${templateToDelete?.name ?? ''}"?`}
        description="Forms already made from it are not affected."
        confirmLabel="Delete template"
        destructive
        onConfirm={() => {
          if (!templateToDelete) return
          templateMutations.remove.mutate(templateToDelete.id, { onSuccess: () => toast.success('Template deleted'), onError: (error) => handleApiError(error) })
          setTemplateToDelete(null)
        }}
      />
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => { if (!open && blocker.state === 'blocked') blocker.reset() }}
        title="Discard unsaved changes?"
        description="Your form changes have not been saved. Leave this page and discard them?"
        cancelLabel="Stay on form"
        confirmLabel="Discard changes"
        destructive
        onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed() }}
      />
    </DndContext>
  )
}
